import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import ActionDropdown from '../../../components/common/action-dropdown';
import InvestorCard from '../../../components/trading/InvestorCard';
import { Filter, Download, Users, DollarSign, TrendingUp } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import TradingStatusBadge from '../../../components/trading/TradingStatusBadge';

type Follower = {
  id: string;
  name: string;
  email: string;
  copiedAmount: number;
  currentProfit: number;
  roi: number;
  joinDate: string;
  status: 'active' | 'inactive' | 'pending';
  traderId: string;
  traderName: string;
  totalDeposits: number;
  totalWithdrawals: number;
};

const CopyTradingFollowers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  const followers: Follower[] = [
    {
      id: 'CFOL001',
      name: 'Alice Williams',
      email: 'alice@example.com',
      copiedAmount: 5000,
      currentProfit: 750,
      roi: 15.0,
      joinDate: '2024-01-15',
      status: 'active',
      traderId: 'TRD001',
      traderName: 'John Smith',
      totalDeposits: 5000,
      totalWithdrawals: 0,
    },
    {
      id: 'CFOL002',
      name: 'Charlie Brown',
      email: 'charlie@example.com',
      copiedAmount: 3000,
      currentProfit: 450,
      roi: 15.0,
      joinDate: '2024-01-10',
      status: 'active',
      traderId: 'TRD002',
      traderName: 'Jane Doe',
      totalDeposits: 3000,
      totalWithdrawals: 0,
    },
    {
      id: 'CFOL003',
      name: 'Diana Miller',
      email: 'diana@example.com',
      copiedAmount: 2000,
      currentProfit: -100,
      roi: -5.0,
      joinDate: '2024-01-08',
      status: 'active',
      traderId: 'TRD003',
      traderName: 'Bob Johnson',
      totalDeposits: 2000,
      totalWithdrawals: 0,
    },
  ];

  const columns: DataTableColumn<Follower>[] = [
    {
      id: 'id',
      header: 'Follower ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'name',
      header: 'Name',
      accessor: row => row.name,
      sortable: true,
      sortValue: row => row.name,
      width: '180px',
    },
    {
      id: 'email',
      header: 'Email',
      accessor: row => row.email,
      sortable: true,
      sortValue: row => row.email,
      width: '220px',
    },
    {
      id: 'traderName',
      header: 'Trader',
      accessor: row => row.traderName,
      sortable: true,
      sortValue: row => row.traderName,
      width: '180px',
    },
    {
      id: 'copiedAmount',
      header: 'Copied',
      accessor: row => `$${row.copiedAmount.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.copiedAmount,
      width: '120px',
    },
    {
      id: 'currentProfit',
      header: 'Current Profit',
      accessor: row => (
        <span
          className={
            row.currentProfit >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          ${row.currentProfit.toLocaleString()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.currentProfit,
      width: '140px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span
          className={
            row.roi >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          {row.roi.toFixed(2)}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.roi,
      width: '100px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <TradingStatusBadge status={row.status} />,
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'joinDate',
      header: 'Joined',
      accessor: row => new Date(row.joinDate).toLocaleDateString(),
      sortable: true,
      sortValue: row => row.joinDate,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          onView={() => console.log('View follower:', row.id)}
          onEdit={() => console.log('Edit follower:', row.id)}
          onDelete={() => console.log('Delete follower:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  const filteredFollowers = followers.filter(follower => {
    return (
      searchTerm === '' ||
      follower.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      follower.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      follower.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalCopied = followers.reduce((sum, fol) => sum + fol.copiedAmount, 0);
  const totalProfit = followers.reduce(
    (sum, fol) => sum + fol.currentProfit,
    0
  );
  const activeFollowers = followers.filter(
    fol => fol.status === 'active'
  ).length;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Follower Management"
          description="Manage copy trading followers"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
          <Users className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Followers</p>
            <p className="text-2xl font-bold text-blue-600">
              {followers.length}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg">
          <Users className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-sm text-muted-foreground">Active Followers</p>
            <p className="text-2xl font-bold text-green-600">
              {activeFollowers}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-purple-50 rounded-lg">
          <DollarSign className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Copied</p>
            <p className="text-2xl font-bold text-purple-600">
              ${totalCopied.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-orange-50 rounded-lg">
          <TrendingUp className="h-5 w-5 text-orange-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Profit</p>
            <p
              className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              ${totalProfit.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('table')}
          >
            Table View
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            Grid View
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <Card>
          <CardHeader>
            <CardTitle>All Followers</CardTitle>
            <CardDescription>
              Manage and monitor all copy trading followers
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={filteredFollowers}
              searchPlaceholder="Search by name, email, or follower ID..."
              onSearchChange={setSearchTerm}
              showColumnVisibility={true}
              showExportButton={false}
              showAddButton={false}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFollowers.map(follower => (
            <InvestorCard
              key={follower.id}
              id={follower.id}
              name={follower.name}
              email={follower.email}
              investedAmount={follower.copiedAmount}
              currentProfit={follower.currentProfit}
              roi={follower.roi}
              joinDate={follower.joinDate}
              status={follower.status}
              onView={() => console.log('View:', follower.id)}
              onEdit={() => console.log('Edit:', follower.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CopyTradingFollowers;
